/**
 * LivePage - Vue temps réel du parc de radars
 * 
 * Affiche UNIQUEMENT les événements reçus en temps réel via WebSocket.
 * La page démarre vide et se remplit au fur et à mesure des événements.
 * Quand presence_detected: false, la carte disparaît.
 * Supporte les événements des radars Vayyar ET des caméras IA Seedoo.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import api, { eventsAPI, aiEventsAPI } from '@/lib/api';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { useAlerts } from '@/contexts/AlertContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/* Inject blink animation once */
if (typeof document !== 'undefined' && !document.getElementById('alert-blink-css')) {
  const style = document.createElement('style');
  style.id = 'alert-blink-css';
  style.textContent = `
    @keyframes blink-red {
      0%, 100% { background-color: #fee2e2; }
      50% { background-color: #fff; }
    }
    @keyframes blink-rose {
      0%, 100% { background-color: #ffe4e6; }
      50% { background-color: #fff; }
    }
    @keyframes blink-green {
      0%, 100% { background-color: #dcfce7; }
      50% { background-color: #fff; }
    }
    @keyframes blink-violet {
      0%, 100% { background-color: #ede9fe; }
      50% { background-color: #fff; }
    }
    .animate-blink-red { animation: blink-red 1.4s ease-in-out infinite; }
    .animate-blink-rose { animation: blink-rose 1.4s ease-in-out infinite; }
    .animate-blink-green { animation: blink-green 1.4s ease-in-out infinite; }
    .animate-blink-violet { animation: blink-violet 1.4s ease-in-out infinite; }
  `;
  document.head.appendChild(style);
}
import { toast } from 'sonner';
import {
  Radio,
  RefreshCw,
  Loader2,
  Wifi,
  WifiOff,
  Activity,
  Camera,
  Video,
  AlertTriangle,
  Eye,
  Clock,
  MapPin,
  UserPlus,
  X,
  CheckSquare,
  XCircle,
  CheckCheck,
  Volume2,
  VolumeX
} from 'lucide-react';
import { EventActionDialog } from '@/components/EventActionDialog';
import { useNavigate } from 'react-router-dom';

const FALL_STATUS_LABELS = {
  fall_detected: { label: 'Suspectée', color: 'bg-orange-500 text-white' },
  fall_confirmed: { label: 'Confirmée', color: 'bg-red-700 text-white' },
  calling: { label: 'Appel en cours', color: 'bg-orange-500 text-white' },
  on_call: { label: 'En communication', color: 'bg-yellow-500 text-black' },
  finished: { label: 'Termine', color: 'bg-green-600 text-white' },
  fall_exit: { label: 'Sortie', color: 'bg-blue-500 text-white' },
  canceled: { label: 'Annule', color: 'bg-gray-500 text-white' },
};

const EVENT_TYPE_CONFIG = {
  FALL: { label: 'CHUTE', color: 'bg-red-600', borderColor: 'border-red-500' },
  SENSITIVE_FALL: { label: 'CHUTE SUSPECTE', color: 'bg-orange-600', borderColor: 'border-orange-500' },
  BED_EXIT: { label: 'SORTIE DE LIT', color: 'bg-amber-600', borderColor: 'border-amber-500' },
};

const AI_WARNING_LABELS = {
  Fall_Detected: { label: 'Chute (IA)', icon: 'alert', color: 'bg-red-600', border: 'border-red-500' },
  Violence_Detected: { label: 'Violence', icon: 'alert', color: 'bg-red-700', border: 'border-red-600' },
  Unattended_Bag: { label: 'Bagage Abandonné', icon: 'alert', color: 'bg-amber-500', border: 'border-amber-400' },
  Open_Door: { label: 'Porte Ouverte', icon: 'eye', color: 'bg-blue-500', border: 'border-blue-400' },
};

function ElapsedTimer({ since }) {
  const [elapsed, setElapsed] = useState('');
  useEffect(() => {
    const update = () => {
      const diff = Math.floor((Date.now() - since) / 1000);
      const mins = Math.floor(diff / 60);
      const secs = diff % 60;
      setElapsed(mins > 0 ? `${mins}m ${secs}s` : `${secs}s`);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [since]);
  return <span className="font-mono">{elapsed}</span>;
}

function formatTime(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d)) return null;
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

// Timestamp de sortie : resolved_at, ou horodatage réel du passage en statut terminal
const TERMINAL_FALL_STATUSES = ['fall_exit', 'finished', 'no_fall'];
function getExitTime(alert) {
  if (alert.resolved_at) return alert.resolved_at;
  if (!TERMINAL_FALL_STATUSES.includes(alert.fall_status)) return null;
  // Horodatage posé par le backend lors de la transition de statut
  if (alert.fall_status_updated_at) return alert.fall_status_updated_at;
  // end_at : présent uniquement si fall_payload.endTimestamp a été transmis
  if (alert.end_at) return alert.end_at;
  // Dernière transition vers un statut terminal dans l'historique
  if (Array.isArray(alert.fall_status_history) && alert.fall_status_history.length) {
    for (let i = alert.fall_status_history.length - 1; i >= 0; i--) {
      const h = alert.fall_status_history[i];
      if (TERMINAL_FALL_STATUSES.includes(h?.status) && h?.timestamp) return h.timestamp;
    }
  }
  // updated_at backend en dernier recours
  if (alert.updated_at) return alert.updated_at;
  return null;
}

function AlertFeedItem({ alert, onAction, onView, selectionMode, isSelected, onToggleSelect }) {
  const isAI = alert.alertSource === 'ai_camera' || alert.type === 'AI_ALERT';
  const isAcked = alert.status === 'ACK' || alert.status === 'ACKNOWLEDGED';
  const radarConfig = EVENT_TYPE_CONFIG[alert.type];
  const aiConfig = isAI ? (AI_WARNING_LABELS[alert.warning_type] || { label: alert.warning_type || 'IA', color: 'bg-violet-600', border: 'border-violet-500' }) : null;
  const config = isAI ? aiConfig : (radarConfig || EVENT_TYPE_CONFIG.FALL);
  const location = isAI
    ? (alert.location_path || alert.channel_name || 'Camera IA')
    : (alert.location_path || alert.sensor_name || alert.radar_name || '—');
  const fallStatus = alert.fall_status ? FALL_STATUS_LABELS[alert.fall_status] : null;
  const confidence = isAI && alert.confidence ? Math.round(alert.confidence * 100) : null;
  const startTime = formatTime(alert.timestamp || alert.occurred_at || alert.created_at);
  const exitTime = formatTime(getExitTime(alert));

  // Background blink class by alert type
  const blinkClass = isAI
    ? 'animate-blink-violet'
    : alert.type === 'FALL' ? 'animate-blink-red'
    : alert.type === 'SENSITIVE_FALL' ? 'animate-blink-rose'
    : alert.type === 'BED_EXIT' ? 'animate-blink-green'
    : 'animate-blink-red';

  return (
    <div
      data-testid={`alert-feed-${alert.id}`}
      className={cn(
        'rounded-lg border px-5 py-3.5 mb-2.5 transition-all',
        selectionMode && isSelected
          ? 'border-blue-400 bg-blue-50/60 dark:border-blue-600 dark:bg-blue-950/30 ring-1 ring-blue-400'
          : isAcked
          ? 'border-gray-200 bg-gray-50/50 dark:border-gray-800 dark:bg-gray-900/30 opacity-60'
          : `border-gray-200 shadow-md ${blinkClass}`
      )}
      onClick={selectionMode ? () => onToggleSelect(alert.id) : undefined}
      style={selectionMode ? { cursor: 'pointer' } : undefined}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        {/* Selection checkbox */}
        {selectionMode && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect(alert.id)}
            onClick={(e) => e.stopPropagation()}
            className="flex-shrink-0 h-4 w-4"
          />
        )}

        {/* Left: info */}
        {isAI ? <Camera className="h-5 w-5 text-violet-500 flex-shrink-0" /> : <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />}
        <Badge className={cn('text-sm font-bold text-white py-0.5 px-2.5', config.color)}>{config.label}</Badge>
        {fallStatus && <Badge className={cn('text-sm py-0.5 px-2', fallStatus.color)}>{fallStatus.label}</Badge>}
        {confidence !== null && <span className="text-sm font-mono text-muted-foreground">{confidence}%</span>}
        {alert.is_simulated && <Badge className="bg-yellow-400/80 text-yellow-900 text-sm py-0.5 px-2">TEST</Badge>}
        {isAcked && <Badge variant="outline" className="text-sm py-0.5 px-2 border-blue-400 text-blue-500">ACK</Badge>}
        <span className="mx-1 text-muted-foreground">|</span>
        <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className="text-sm font-semibold truncate flex-1 min-w-0">{location}</span>
        {isAI && alert.warning_text && <span className="text-xs text-muted-foreground italic">{alert.warning_text}</span>}
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground flex-shrink-0">
          <Clock className="h-4 w-4" />
          {startTime && (
            <span className="font-mono text-foreground">{startTime}</span>
          )}
          {exitTime ? (
            <>
              <span className="text-muted-foreground">→</span>
              <span className="font-mono text-green-600 dark:text-green-400">{exitTime}</span>
            </>
          ) : (
            <>
              <span className="text-muted-foreground">·</span>
              <ElapsedTimer since={alert.addedAt || Date.now()} />
            </>
          )}
        </div>

        {/* Right: actions (hidden in selection mode) */}
        {!selectionMode && (
          <div className="ml-auto flex items-center gap-2 flex-shrink-0">
            {!isAcked && <Button size="sm" variant="outline" className="h-8 text-sm px-3" onClick={() => onAction(alert, 'ACK')}>Acquitter</Button>}
            <Button size="sm" variant="ghost" className="h-8 text-sm px-3" onClick={() => onAction(alert, 'FALSE_ALARM')}>Fausse alerte</Button>
            {isAI && alert.video_url && <Button size="sm" variant="outline" className="h-8 text-sm px-3" onClick={() => window.open(alert.video_url, '_blank')}><Video className="h-4 w-4 mr-1" />Video</Button>}
            <Button size="sm" variant="ghost" className="h-8 text-sm px-3" onClick={() => onView(alert)}><Eye className="h-4 w-4 mr-1" />Details</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ActiveAlertsSection() {
  const { activeAlerts, updateAlert, dismissAlert } = useAlerts();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState('ACK');
  const [dialogEvent, setDialogEvent] = useState(null);
  const [filter, setFilter] = useState('all');

  // Selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Bulk false alarm dialog
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('ACK');
  const [bulkComment, setBulkComment] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  const handleAction = (alert, action) => {
    setDialogEvent(alert);
    setDialogAction(action);
    setDialogOpen(true);
  };

  const handleActionSuccess = (updatedEvent) => {
    if (!updatedEvent) return;
    // ACK : l'alerte reste dans le fil (grisée) jusqu'à résolution ; RESOLVED / FALSE_ALARM : retirée
    if (dialogAction === 'RESOLVED' || dialogAction === 'FALSE_ALARM') {
      dismissAlert(updatedEvent.id);
    } else {
      const { status, assigned_to, assigned_to_name } = updatedEvent;
      updateAlert(updatedEvent.id, { status, assigned_to, assigned_to_name });
    }
  };

  const handleView = (alert) => {
    if (alert.alertSource === 'ai_camera') {
      navigate('/ai-sensors');
    } else {
      navigate(`/events/${alert.id}`);
    }
  };

  const toggleFilter = (f) => {
    const next = filter === f ? 'all' : f;
    setFilter(next);
  };

  const toggleSelectionMode = () => {
    setSelectionMode(v => !v);
    setSelectedIds(new Set());
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(sorted.map(a => a.id)));
  };

  const deselectAll = () => setSelectedIds(new Set());

  const openBulkAction = (action) => {
    setBulkAction(action);
    setBulkComment('');
    setBulkDialogOpen(true);
  };

  const executeBulkAction = async () => {
    if (bulkAction === 'FALSE_ALARM' && !bulkComment.trim()) {
      toast.error('Le commentaire est obligatoire pour une fausse alarme');
      return;
    }
    setBulkLoading(true);
    try {
      const ids = [...selectedIds];
      const res = await eventsAPI.bulkUpdate(ids, bulkAction, bulkComment.trim() || undefined);
      const { success_count, failed_count } = res.data;
      if (success_count > 0) {
        ids.forEach(id => bulkAction === 'ACK' ? updateAlert(id, { status: 'ACK' }) : dismissAlert(id));
        toast.success(`${success_count} alerte(s) traitée(s)`);
      }
      if (failed_count > 0) {
        toast.warning(`${failed_count} alerte(s) non traitée(s)`);
      }
      setBulkDialogOpen(false);
      setSelectionMode(false);
      setSelectedIds(new Set());
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur lors du traitement groupé';
      toast.error(msg);
    } finally {
      setBulkLoading(false);
    }
  };

  if (activeAlerts.length === 0) return null;

  const unackedCount = activeAlerts.filter(a => a.status !== 'ACK' && a.status !== 'ACKNOWLEDGED').length;
  const radarCount = activeAlerts.filter(a => a.alertSource !== 'ai_camera').length;
  const aiCount = activeAlerts.filter(a => a.alertSource === 'ai_camera').length;

  // Count by event type
  const typeCounts = {};
  activeAlerts.forEach(a => {
    const key = a.alertSource === 'ai_camera' ? (a.warning_type || 'AI') : (a.type || 'FALL');
    typeCounts[key] = (typeCounts[key] || 0) + 1;
  });

  // Apply filter
  const filtered = activeAlerts.filter(a => {
    if (filter === 'all') return true;
    if (filter === 'pending') return a.status !== 'ACK' && a.status !== 'ACKNOWLEDGED';
    if (filter === 'radar') return a.alertSource !== 'ai_camera';
    if (filter === 'ai') return a.alertSource === 'ai_camera';
    if (a.alertSource === 'ai_camera') return a.warning_type === filter;
    return a.type === filter;
  });

  // Sort by time - newest first
  const sorted = [...filtered].sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));

  const allSelected = sorted.length > 0 && sorted.every(a => selectedIds.has(a.id));

  return (
    <>
      <Card className="border-red-500/40 shadow-lg overflow-hidden flex flex-col" data-testid="active-alerts-section">
        <CardHeader className="border-b border-red-500/20 py-3 px-5 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/20">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="flex items-center gap-2 text-base font-semibold text-red-700 dark:text-red-400">
              <div className="relative">
                <AlertTriangle className="h-5 w-5" />
                <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600" />
                </span>
              </div>
              Fil d'alertes ({activeAlerts.length})
            </CardTitle>
            {/* Selection toggle */}
            <Button
              size="sm"
              variant={selectionMode ? 'default' : 'outline'}
              className={cn('h-7 text-xs px-2.5', selectionMode && 'bg-blue-600 hover:bg-blue-700')}
              onClick={toggleSelectionMode}
            >
              <CheckSquare className="h-3.5 w-3.5 mr-1" />
              {selectionMode ? 'Annuler' : 'Sélectionner'}
            </Button>
          </div>

          {/* Bulk action bar (visible in selection mode) */}
          {selectionMode && (
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-red-200/50 flex-wrap">
              <Checkbox
                checked={allSelected}
                onCheckedChange={allSelected ? deselectAll : selectAll}
                className="h-4 w-4"
              />
              <span className="text-xs text-muted-foreground">
                {selectedIds.size === 0
                  ? 'Aucune sélection'
                  : `${selectedIds.size} sélectionnée(s)`}
              </span>
              {sorted.length > 0 && (
                <Button size="sm" variant="ghost" className="h-6 text-xs px-2 text-blue-600" onClick={allSelected ? deselectAll : selectAll}>
                  {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                </Button>
              )}
              {selectedIds.size > 0 && (
                <>
                  <span className="w-px h-4 bg-border" />
                  <Button
                    size="sm"
                    className="h-7 text-xs px-3 bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => openBulkAction('ACK')}
                  >
                    <CheckCheck className="h-3.5 w-3.5 mr-1" />
                    Acquitter ({selectedIds.size})
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs px-3 border-gray-400 text-gray-700 hover:bg-gray-100"
                    onClick={() => openBulkAction('FALSE_ALARM')}
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1" />
                    Fausse alarme ({selectedIds.size})
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Filter badges */}
          {!selectionMode && (
            <div className="flex items-center gap-2 flex-wrap mt-2">
              {unackedCount > 0 && (
                <Badge
                  data-testid="filter-pending"
                  className={cn(
                    'text-xs cursor-pointer transition-all select-none py-0.5 px-2',
                    filter === 'pending'
                      ? 'bg-red-600 text-white ring-2 ring-red-400 ring-offset-1'
                      : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-300'
                  )}
                  onClick={() => toggleFilter('pending')}
                >
                  {unackedCount} en attente
                </Badge>
              )}
              {radarCount > 0 && (
                <Badge
                  data-testid="filter-radar"
                  className={cn(
                    'text-xs cursor-pointer transition-all select-none py-0.5 px-2',
                    filter === 'radar'
                      ? 'bg-red-600 text-white ring-2 ring-red-400 ring-offset-1'
                      : 'bg-transparent border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30'
                  )}
                  onClick={() => toggleFilter('radar')}
                >
                  <Radio className="h-3 w-3 mr-0.5" />{radarCount} radar
                </Badge>
              )}
              {aiCount > 0 && (
                <Badge
                  data-testid="filter-ai"
                  className={cn(
                    'text-xs cursor-pointer transition-all select-none py-0.5 px-2',
                    filter === 'ai'
                      ? 'bg-violet-600 text-white ring-2 ring-violet-400 ring-offset-1'
                      : 'bg-transparent border border-violet-300 text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-950/30'
                  )}
                  onClick={() => toggleFilter('ai')}
                >
                  <Camera className="h-3 w-3 mr-0.5" />{aiCount} IA
                </Badge>
              )}
              <span className="w-px h-4 bg-border mx-0.5" />
              {Object.entries(typeCounts).map(([type, count]) => {
                const radarCfg = EVENT_TYPE_CONFIG[type];
                const aiCfg = AI_WARNING_LABELS[type];
                const label = radarCfg?.label || aiCfg?.label || type;
                const isActive = filter === type;
                return (
                  <Badge
                    key={type}
                    data-testid={`filter-${type}`}
                    className={cn(
                      'text-xs cursor-pointer transition-all select-none py-0.5 px-2',
                      isActive
                        ? cn('text-white ring-2 ring-offset-1', radarCfg?.color || aiCfg?.color || 'bg-gray-600', radarCfg ? 'ring-red-400' : 'ring-violet-400')
                        : 'bg-transparent border border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-900'
                    )}
                    onClick={() => toggleFilter(type)}
                  >
                    {label} ({count})
                  </Badge>
                );
              })}
              {filter !== 'all' && (
                <Badge
                  data-testid="filter-clear"
                  className="text-xs cursor-pointer bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-400 select-none py-0.5 px-2"
                  onClick={() => setFilter('all')}
                >
                  <X className="h-3 w-3 mr-0.5" />Tout
                </Badge>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent className="p-4 flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
          {sorted.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Aucune alerte pour ce filtre
            </div>
          ) : sorted.map(alert => (
            <AlertFeedItem
              key={alert.id}
              alert={alert}
              onAction={handleAction}
              onView={handleView}
              selectionMode={selectionMode}
              isSelected={selectedIds.has(alert.id)}
              onToggleSelect={toggleSelectOne}
            />
          ))}
        </CardContent>
      </Card>

      {/* Single event action dialog */}
      <EventActionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        eventId={dialogEvent?.id}
        action={dialogAction}
        eventInfo={dialogEvent ? {
          type: dialogEvent.alertSource === 'ai_camera'
            ? (AI_WARNING_LABELS[dialogEvent.warning_type]?.label || dialogEvent.warning_type)
            : dialogEvent.type,
          location: dialogEvent.location_path || dialogEvent.channel_name || dialogEvent.radar_name || 'Localisation inconnue',
          sensor: dialogEvent.alertSource === 'ai_camera' ? dialogEvent.channel_name : dialogEvent.radar_name,
          isAI: dialogEvent.alertSource === 'ai_camera'
        } : null}
        onSuccess={handleActionSuccess}
      />

      {/* Bulk action dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {bulkAction === 'ACK'
                ? <><CheckCheck className="h-5 w-5 text-blue-600" />Acquitter {selectedIds.size} alerte(s)</>
                : <><XCircle className="h-5 w-5 text-gray-600" />Fausse alarme — {selectedIds.size} alerte(s)</>}
            </DialogTitle>
            <DialogDescription>
              {bulkAction === 'ACK'
                ? 'Toutes les alertes sélectionnées seront acquittées.'
                : 'Toutes les alertes sélectionnées seront marquées comme fausses alarmes. Un commentaire est obligatoire.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="bulk-comment">
                Commentaire {bulkAction === 'FALSE_ALARM' && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                id="bulk-comment"
                placeholder={bulkAction === 'FALSE_ALARM' ? 'Commentaire obligatoire...' : 'Commentaire optionnel...'}
                value={bulkComment}
                onChange={(e) => setBulkComment(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)} disabled={bulkLoading}>
              Annuler
            </Button>
            <Button
              className={bulkAction === 'ACK' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-700 hover:bg-gray-800'}
              onClick={executeBulkAction}
              disabled={bulkLoading}
            >
              {bulkLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {bulkAction === 'ACK' ? 'Acquitter' : 'Confirmer fausse alarme'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function LivePage() {
  const { subscribe, connected } = useWebSocket();
  const { soundEnabled, setSoundEnabled } = useAlerts();
  
  // États
  const [events, setEvents] = useState([]);
  const [aiEvents, setAiEvents] = useState([]);
  const [radarStatuses, setRadarStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [newEventIds, setNewEventIds] = useState(new Set());
  
  const isFetchingRef = useRef(false);

  // Chargement des métadonnées uniquement (pas d'événements historiques)
  // La page démarre vide et se remplit via WebSocket temps réel
  const fetchData = useCallback(async () => {
    if (isFetchingRef.current) return;
    
    isFetchingRef.current = true;
    setLoading(true);
    
    try {
      const sensorsRes = await api.get('/sensors');
      
      setEvents([]);
      
      // Statuts des radars (pour notifications online/offline)
      const statuses = {};
      sensorsRes.data.forEach(sensor => {
        statuses[sensor.id] = {
          status: sensor.status,
          deviceOnline: sensor.status === 'ONLINE',
          name: sensor.name,
          serial_product: sensor.serial_product
        };
      });
      setRadarStatuses(statuses);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // WebSocket pour les mises à jour temps réel
  const radarStatusesRef = useRef(radarStatuses);
  useEffect(() => {
    radarStatusesRef.current = radarStatuses;
  }, [radarStatuses]);

  useEffect(() => {
    const unsubscribe = subscribe('live', (message) => {
      if (message.type === 'force_refresh') {
        if (message.events && Array.isArray(message.events)) {
          const uniqueEvents = [];
          const seenIds = new Set();
          for (const event of message.events) {
            if (!seenIds.has(event.id)) {
              seenIds.add(event.id);
              uniqueEvents.push(event);
            }
          }
          setEvents(uniqueEvents);
        }
        return;
      }
      
      // Mise à jour de l'état de présence - gérer la disparition des cartes
      if (message.type === 'presence_state_update' || message.type === 'presence_update') {
        const { sensor_id, device_id, presence_detected, presenceDetected } = message;
        const sensorId = sensor_id || device_id;
        const hasPresence = presence_detected ?? presenceDetected;
        
        if (sensorId && hasPresence === false) {
          // Présence disparue - supprimer la carte
          setEvents(prev => prev.filter(e => {
            const eventSensorId = e.sensor_id || e.device_id;
            return eventSensorId !== sensorId;
          }));
        }
        return;
      }
      
      // Mise à jour du statut d'un radar
      if (message.type === 'sensor_status') {
        const { sensor_id, status } = message;
        setRadarStatuses(prev => ({
          ...prev,
          [sensor_id]: {
            ...prev[sensor_id],
            status: status,
            deviceOnline: status === 'ONLINE'
          }
        }));
        
        if (status === 'OFFLINE') {
          const prevStatus = radarStatusesRef.current[sensor_id];
          toast.warning('Radar hors ligne', {
            description: prevStatus?.name || sensor_id,
            duration: 5000
          });
        }
      }
      // Nouvel événement
      else if (message.type === 'new_event' || message.type === 'new_radar_event') {
        const newEvent = message.event;
        if (!newEvent) return;
        
        const sensorId = newEvent.sensor_id || newEvent.device_id;
        
        // Si presence_detected === false, SUPPRIMER la carte du radar
        if (newEvent.type === 'PRESENCE' && newEvent.presence_detected === false) {
          setEvents(prev => prev.filter(e => {
            const eventSensorId = e.sensor_id || e.device_id;
            // Supprimer tous les événements de ce sensor
            return eventSensorId !== sensorId;
          }));
          return;
        }
        
        // Sinon, ajouter/mettre à jour l'événement
        setEvents(prev => {
          // Supprimer les anciens événements du même sensor pour éviter les doublons
          const filtered = prev.filter(e => {
            const eventSensorId = e.sensor_id || e.device_id;
            return eventSensorId !== sensorId;
          });
          return [newEvent, ...filtered.slice(0, 99)];
        });
        
        setNewEventIds(prev => new Set([...prev, newEvent.id]));
        setTimeout(() => {
          setNewEventIds(prev => {
            const next = new Set(prev);
            next.delete(newEvent.id);
            return next;
          });
        }, 3000);
        
        // Alerte pour chutes
        if (newEvent.type === 'FALL') {
          toast.warning('⚠️ Chute suspectée', {
            description: newEvent.location_path || newEvent.radar_name || 'Localisation inconnue',
            duration: 8000
          });
        }
      }
      // Nouvel événement IA (caméra Seedoo)
      else if (message.type === 'new_ai_event' || message.event_source === 'ai_camera') {
        const aiEvent = message.event || message;
        if (!aiEvent) return;
        
        // Ajouter à la liste des événements IA
        setAiEvents(prev => [aiEvent, ...prev.slice(0, 49)]);
        
        setNewEventIds(prev => new Set([...prev, aiEvent.id]));
        setTimeout(() => {
          setNewEventIds(prev => {
            const next = new Set(prev);
            next.delete(aiEvent.id);
            return next;
          });
        }, 3000);
        
        // Alertes critiques IA
        const criticalTypes = ['Fall_Detected', 'Violence_Detected'];
        if (criticalTypes.includes(aiEvent.warning_type)) {
          const alertMessages = {
            'Fall_Detected': '🚨 Chute détectée (IA)!',
            'Violence_Detected': '⚠️ Violence détectée!',
          };
          toast.error(alertMessages[aiEvent.warning_type] || '⚠️ Alerte IA!', {
            description: aiEvent.channel_name || aiEvent.location_path || 'Caméra IA',
            duration: 15000,
            action: aiEvent.video_url ? {
              label: 'Voir vidéo',
              onClick: () => window.open(aiEvent.video_url, '_blank')
            } : undefined
          });
        }
      }
      else if (message.type === 'fall_event_update') {
        const { event_id, fall_status, sensor_name, timestamp } = message;
        if (fall_status === 'fall_confirmed') {
          toast.error('🚨 Chute confirmée!', {
            description: sensor_name || 'Intervention requise',
            duration: 10000
          });
        }
        setEvents(prev => prev.map(e =>
          e.id === event_id ? { ...e, fall_status, fall_status_updated_at: timestamp } : e
        ));
      }
      else if (message.type === 'event_updated') {
        setEvents(prev => prev.map(e => 
          e.id === message.event_id ? { ...e, ...message.update } : e
        ));
      }
      else if (message.type === 'sensor_registered') {
        const { sensor } = message;
        setRadarStatuses(prev => ({
          ...prev,
          [sensor.id]: {
            status: sensor.status,
            deviceOnline: sensor.status === 'ONLINE',
            name: sensor.name
          }
        }));
      }
    });
    return unsubscribe;
  }, [subscribe]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="live-page" className="flex flex-col h-[calc(100vh-140px)]">
      {/* Header compact - indicateur temps réel et bouton refresh */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Activity className="h-5 w-5 text-primary" />
            {connected && (
              <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
              </span>
            )}
          </div>
          
          <Badge variant="outline" className={cn(
            "text-xs font-normal",
            connected ? "border-green-500 text-green-600" : "border-amber-500 text-amber-600"
          )}>
            {connected ? (
              <><Wifi className="h-3 w-3 mr-1" />Live</>
            ) : (
              <><WifiOff className="h-3 w-3 mr-1" />...</>
            )}
          </Badge>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant={soundEnabled ? 'outline' : 'secondary'}
            size="sm"
            onClick={() => setSoundEnabled(v => !v)}
            title={soundEnabled ? 'Désactiver le son des alertes' : 'Activer le son des alertes'}
            data-testid="sound-toggle-btn"
          >
            {soundEnabled
              ? <Volume2 className="h-4 w-4 text-green-600" />
              : <VolumeX className="h-4 w-4 text-muted-foreground" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* Fil d'alertes en temps réel - source unique, pleine page */}
      <div className="flex-1 min-h-0">
        <ActiveAlertsSection />
      </div>
    </div>
  );
}

export default LivePage;
